import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMedia } from "react-use";

import { IHttpRequestConfig } from "@/browser/http";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import {
  fetchAssignmentRevisionsList,
  IAssignment,
  IAssignmentRevision,
  IFetchAssignmentRevisionsListParams,
} from "@/data/assignments";
import { Flex, IFlexProps } from "@/eworkitComponents/components";
import { useFetcher } from "@/hooks/http";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { router } from "@/views/router";

import { AssignmentWorkOrdersList } from "./AssignmentWorkOrdersList";
import { AssignmentWorkOrdersTable } from "./AssignmentWorkOrdersTable";

export interface IAssignmentWorkOrdersProps extends Partial<IFlexProps> {
  assignment: IAssignment;
}

export const AssignmentWorkOrders: FC<IAssignmentWorkOrdersProps> = memo(({ assignment, ...props }) => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);
  const history = useHistory();
  const { currentLocationState } = usePreviousRoutePath();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const { payload: revisions, working } = useFetcher(
    (params: Partial<IFetchAssignmentRevisionsListParams>, httpConfig: IHttpRequestConfig) =>
      fetchAssignmentRevisionsList(
        { companyId: contextCompany?.id, assignmentId: assignment.id, ...params },
        httpConfig,
      ),
    {},
    [contextCompany?.id, assignment.id],
  );

  const handleOnClick = useCallback(
    (rowData: IAssignmentRevision) =>
      history.push(router.get("work-orders.detail", { workOrderId: rowData.id }).path, currentLocationState),
    [],
  );

  return (
    <Flex direction="column" {...props}>
      <Flex>
        <Text size="xl" weight="bold">
          {t("ASSIGNMENTS.DETAILS.WORK_ORDERS_HEADER")}
        </Text>
      </Flex>

      {working && (
        <Flex margin={["xxl", false]} align="center" justify="center">
          <LoadingSpinner size="lg" variant="primary" />
        </Flex>
      )}

      {!working && revisions && (
        <Flex direction="column" margin={["md", false, false, false]}>
          {isSmallScreen ? (
            <AssignmentWorkOrdersList workOrders={revisions} onRowClick={handleOnClick} />
          ) : (
            <AssignmentWorkOrdersTable workOrders={revisions} onRowClick={handleOnClick} />
          )}
        </Flex>
      )}
    </Flex>
  );
});
